import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  IconButton,
  Switch,
  Tabs,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Ticket, Copy, Lock } from "lucide-react";
import toast from "react-hot-toast";
import Modal from "@/components/ui/Modal";
import SideDrawer from "@/components/ui/SideDrawer";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import DateField from "@/components/form/DateField";
import { useQuery } from "@tanstack/react-query";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import {
  SubscriptionPlan,
  Offer,
  OfferCode,
  OfferRedemption,
  OFFER_STATUSES,
} from "@/types/breeze/AnythinkPay";
import { PaginatedResult } from "@/types/breeze/Any";
import {
  useAnythinkPayOffers,
  useAnythinkPayOfferRedemptions,
  useCreateAnythinkPayOffer,
  useCreateAnythinkPayOfferCode,
  useUpdateAnythinkPayOffer,
  useTenantRedemptions,
} from "@/hooks/useAnythinkPayOffers";
import { formatDate, formatRelativeDate } from "@/utils/date";
import {
  EmptyStateCard,
  RewardForm,
  RewardPickerSection,
  StatusBadge,
  parseReward,
  rewardStatusColor,
  rewardSummary,
  serialiseReward,
} from "./_shared";
import Select from "@/components/ui/Select";
import {
  EligibilityFormState,
  emptyEligibility,
  eligibilityFormToJson,
  eligibilityFromJson,
} from "./eligibility";

type PromoFormState = {
  name: string;
  slug: string;
  redeemer: RewardForm;
  valid_from: string;
  valid_until: string;
  total_cap_unlimited: boolean;
  total_redemption_cap: string;
  per_user_cap_unlimited: boolean;
  per_user_redemption_cap: number;
  status: "active" | "paused" | "expired";
  eligibility: EligibilityFormState;
};

const emptyForm = (): PromoFormState => ({
  name: "",
  slug: "",
  redeemer: { type: "trial_extension", days: 14 },
  valid_from: "",
  valid_until: "",
  total_cap_unlimited: true,
  total_redemption_cap: "",
  per_user_cap_unlimited: false,
  per_user_redemption_cap: 1,
  status: "active",
  eligibility: emptyEligibility(),
});

const formFromOffer = (offer: Offer): PromoFormState => ({
  name: offer.name,
  slug: "",
  redeemer: parseReward(offer.redeemer_reward_json) ?? { type: "none" },
  valid_from: offer.valid_from?.slice(0, 10) ?? "",
  valid_until: offer.valid_until?.slice(0, 10) ?? "",
  total_cap_unlimited: offer.total_redemption_cap == null,
  total_redemption_cap: offer.total_redemption_cap?.toString() ?? "",
  per_user_cap_unlimited: isUnlimitedPerUser(offer.per_user_redemption_cap),
  per_user_redemption_cap: isUnlimitedPerUser(offer.per_user_redemption_cap)
    ? 1
    : offer.per_user_redemption_cap,
  eligibility: eligibilityFromJson(offer.eligibility_json),
  status: offer.status,
});

const kindFromReward = (reward: RewardForm): "discount" | "trial_extension" =>
  reward.type === "discount" ? "discount" : "trial_extension";

// Sentinel: per-user cap is a non-nullable int on the API side. We send int.MaxValue when the
// user picks "Unlimited" and read it back the same way. Switching the column to nullable would
// be cleaner but is a separate migration.
const UNLIMITED_PER_USER_CAP = 2147483647;
const isUnlimitedPerUser = (n: number) => n >= UNLIMITED_PER_USER_CAP;

type PromoRow = {
  offer: Offer;
  code: OfferCode | null;
};

type PromoCodesProps = {
  isCreateOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
};

export default function PromoCodes({
  isCreateOpen,
  onCreateOpenChange,
}: PromoCodesProps = {}) {
  const { data: offers, isLoading } = useAnythinkPayOffers();
  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const createOpen = isCreateOpen ?? internalCreateOpen;
  const setCreateOpen = onCreateOpenChange ?? setInternalCreateOpen;
  const [activeRow, setActiveRow] = useState<PromoRow | null>(null);

  const promoOffers = useMemo(
    () => (offers ?? []).filter((o) => o.kind !== "referral"),
    [offers],
  );

  if (isLoading) return <LoadingScreen fullPage={false} />;

  if (promoOffers.length === 0) {
    return (
      <>
        <EmptyStateCard
          icon={<Ticket size={36} />}
          title="No promo codes yet"
          description="One-off codes you publish — LAUNCH50, SUMMER, partner deals — each with its own reward, validity window, and redemption cap."
          cta={null}
        />
        <PromoCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      </>
    );
  }

  const columns: DataGridColumn<Offer>[] = [
    {
      field: "primary_code",
      header: "Code",
      flex: 1.4,
      minWidth: 220,
      cellRenderer: (o) => (
        <Flex direction="column" gap="1">
          <Flex align="center" gap="2">
            {o.primary_code ? (
              <Badge size="2" variant="soft" color="indigo" style={{ fontFamily: "monospace" }}>
                {o.primary_code.slug}
              </Badge>
            ) : (
              <Text size="2" color="gray">No code attached</Text>
            )}
            {o.eligibility_json && o.eligibility_json !== "null" && (
              <Badge size="1" variant="soft" color="amber" title="Eligibility rules apply">
                <Lock size={10} style={{ marginRight: 2 }} />
                Restricted
              </Badge>
            )}
          </Flex>
          <Text size="1" color="gray">{o.name}</Text>
        </Flex>
      ),
      valueGetter: (o) => o.primary_code?.slug ?? o.name,
    },
    {
      field: "reward",
      header: "Reward",
      flex: 1,
      minWidth: 150,
      cellRenderer: (o) => <Text size="2">{rewardSummary(o.redeemer_reward_json)}</Text>,
      valueGetter: (o) => rewardSummary(o.redeemer_reward_json),
    },
    {
      field: "valid",
      header: "Valid",
      flex: 1.2,
      minWidth: 170,
      cellRenderer: (o) => (
        <Text size="2" color="gray">
          {o.valid_from || o.valid_until
            ? `${o.valid_from ? formatDate(o.valid_from) : "—"} → ${
                o.valid_until ? formatDate(o.valid_until) : "—"
              }`
            : "Always"}
        </Text>
      ),
    },
    {
      field: "redemptions",
      header: "Redemptions",
      flex: 0.7,
      minWidth: 110,
      cellRenderer: (o) => (
        <Text size="2" weight="medium">
          {o.primary_code?.redemption_count ?? 0}
        </Text>
      ),
      valueGetter: (o) => String(o.primary_code?.redemption_count ?? 0),
    },
    {
      field: "status",
      header: "Status",
      flex: 0.7,
      minWidth: 110,
      cellRenderer: (o) => <StatusBadge status={o.status} />,
    },
    {
      field: "created_at",
      header: "Created",
      flex: 0.9,
      minWidth: 130,
      cellRenderer: (o) => (
        <Text size="2" color="gray">{formatRelativeDate(o.created_at)}</Text>
      ),
    },
  ];

  const paginated: PaginatedResult<Offer> = {
    items: promoOffers,
    page: 1,
    page_size: promoOffers.length,
    total_items: promoOffers.length,
    total_pages: 1,
    has_next_page: false,
    has_previous_page: false,
  };

  return (
    <Flex direction="column" gap="4">
      <Tabs.Root defaultValue="codes">
        <Tabs.List>
          <Tabs.Trigger value="codes">Codes</Tabs.Trigger>
          <Tabs.Trigger value="redemptions">Redemptions</Tabs.Trigger>
        </Tabs.List>

        <Box mt="3">
          <Tabs.Content value="codes">
            <Box style={{ height: 480 }}>
              <DataGrid<Offer>
                data={paginated}
                columns={columns}
                storageKey="promo-codes-list"
                currentPage={1}
                currentPageSize={promoOffers.length}
                onPageChange={() => {}}
                onPageSizeChange={() => {}}
                rowSelection="none"
                showPagination={false}
                onCellClicked={(_, o) => setActiveRow({ offer: o, code: o.primary_code ?? null })}
              />
            </Box>
          </Tabs.Content>
          <Tabs.Content value="redemptions">
            <PromoRedemptionsAcrossAllCodes />
          </Tabs.Content>
        </Box>
      </Tabs.Root>

      <PromoCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <PromoDetailDrawer row={activeRow} onClose={() => setActiveRow(null)} />
    </Flex>
  );
}

function PromoRedemptionsAcrossAllCodes() {
  const { data: rows, isLoading } = useTenantRedemptions(["trial_extension", "discount"]);

  if (isLoading) return <LoadingScreen fullPage={false} />;

  const items = rows ?? [];
  if (items.length === 0) {
    return (
      <Text size="2" color="gray">
        No promo code redemptions yet. Redemptions accrue here as users enter
        your codes via the app or checkout.
      </Text>
    );
  }

  const columns: DataGridColumn<OfferRedemption>[] = [
    {
      field: "user",
      header: "User",
      flex: 1.2,
      minWidth: 200,
      cellRenderer: (r) => (
        <Flex direction="column">
          <Text size="2" weight="medium">
            {r.redeemer
              ? `${r.redeemer.first_name} ${r.redeemer.last_name}`.trim()
              : `#${r.redeemer_user_id}`}
          </Text>
          {r.redeemer?.email && (
            <Text size="1" color="gray">{r.redeemer.email}</Text>
          )}
        </Flex>
      ),
      valueGetter: (r) =>
        r.redeemer
          ? `${r.redeemer.first_name} ${r.redeemer.last_name}`.trim()
          : `#${r.redeemer_user_id}`,
    },
    {
      field: "code",
      header: "Code",
      flex: 0.8,
      minWidth: 130,
      cellRenderer: (r) =>
        r.code_slug ? (
          <Badge size="1" variant="soft" color="indigo" style={{ fontFamily: "monospace" }}>
            {r.code_slug}
          </Badge>
        ) : (
          <Text size="2" color="gray">—</Text>
        ),
      valueGetter: (r) => r.code_slug ?? "",
    },
    {
      field: "offer_name",
      header: "Offer",
      flex: 1.2,
      minWidth: 160,
      cellRenderer: (r) => <Text size="2">{r.offer_name ?? "—"}</Text>,
      valueGetter: (r) => r.offer_name ?? "",
    },
    {
      field: "reward_status",
      header: "Reward",
      flex: 0.7,
      minWidth: 100,
      cellRenderer: (r) => (
        <Badge color={rewardStatusColor(r.redeemer_reward_status)} variant="soft">
          {r.redeemer_reward_status}
        </Badge>
      ),
    },
    {
      field: "redeemed_at",
      header: "When",
      flex: 1,
      minWidth: 160,
      cellRenderer: (r) => (
        <Text size="2" color="gray">{formatDate(r.redeemed_at)}</Text>
      ),
    },
  ];

  const paginated: PaginatedResult<OfferRedemption> = {
    items,
    page: 1,
    page_size: items.length,
    total_items: items.length,
    total_pages: 1,
    has_next_page: false,
    has_previous_page: false,
  };

  return (
    <Box style={{ height: 480 }}>
      <DataGrid<OfferRedemption>
        data={paginated}
        columns={columns}
        storageKey="promo-redemptions-list"
        currentPage={1}
        currentPageSize={items.length}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        rowSelection="none"
        showPagination={false}
      />
    </Box>
  );
}

function PromoCreateModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const createOffer = useCreateAnythinkPayOffer();
  const createCode = useCreateAnythinkPayOfferCode();
  const [state, setState] = useState<PromoFormState>(emptyForm());

  useEffect(() => {
    if (open) setState(emptyForm());
  }, [open]);

  const submit = async () => {
    if (!state.slug.trim()) {
      toast.error("Code is required");
      return;
    }
    const redeemerJson = serialiseReward(state.redeemer);
    if (!redeemerJson) {
      toast.error("Choose a reward");
      return;
    }
    const slug = state.slug.trim().toUpperCase();
    const displayName = state.name.trim() || slug;
    const totalCap = state.total_cap_unlimited
      ? null
      : state.total_redemption_cap.trim()
      ? parseInt(state.total_redemption_cap, 10)
      : null;
    const perUserCap = state.per_user_cap_unlimited
      ? UNLIMITED_PER_USER_CAP
      : state.per_user_redemption_cap;

    try {
      const offer = await createOffer.mutateAsync({
        name: displayName,
        description: null,
        kind: kindFromReward(state.redeemer),
        redeemer_reward_json: redeemerJson,
        referrer_reward_json: null,
        eligibility_json: eligibilityFormToJson(state.eligibility),
        valid_from: state.valid_from || null,
        valid_until: state.valid_until || null,
        total_redemption_cap: totalCap,
        per_user_redemption_cap: perUserCap,
        status: state.status,
      });
      await createCode.mutateAsync({ id: offer.id, slug });
      onClose();
    } catch {
      // toast handled in service
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="New promo code"
      size="md"
      modal={false}
      actions={
        <Flex gap="2" justify="end">
          <Button variant="soft" color="gray" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            loading={createOffer.isPending || createCode.isPending}
          >
            Create promo code
          </Button>
        </Flex>
      }
    >
      <PromoFormBody state={state} setState={setState} isCreate />
    </Modal>
  );
}

function PromoFormBody({
  state,
  setState,
  isCreate,
  currentOfferId,
}: {
  state: PromoFormState;
  setState: (s: PromoFormState) => void;
  isCreate?: boolean;
  currentOfferId?: string | null;
}) {
  return (
    <Flex direction="column" gap="4" p="2">
      {isCreate && (
        <Box>
          <Text size="2" weight="medium" as="div" mb="1">Code</Text>
          <TextField.Root
            value={state.slug}
            onChange={(e) =>
              setState({ ...state, slug: e.target.value.toUpperCase() })
            }
            placeholder="LAUNCH50"
            style={{ fontFamily: "monospace" }}
          />
          <Text size="1" color="gray" as="div" mt="1">
            The string users will type at checkout or in the app.
          </Text>
        </Box>
      )}

      <Box>
        <Text size="2" weight="medium" as="div" mb="1">
          Internal name {!isCreate && "(visible to admins only)"}
        </Text>
        <TextField.Root
          value={state.name}
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder={isCreate ? "Defaults to the code" : "Promo: LAUNCH50"}
        />
      </Box>

      <RewardPickerSection
        label="Reward"
        reward={state.redeemer}
        onChange={(r) => setState({ ...state, redeemer: r })}
        audience="redeemer"
      />

      <Grid columns="2" gap="3">
        <Box>
          <Text size="2" weight="medium" as="div" mb="1">
            Valid from <Text size="1" color="gray">(optional)</Text>
          </Text>
          <DateField
            value={state.valid_from}
            onChange={(v) => setState({ ...state, valid_from: v })}
            placeholder="Any time"
            maxDate={state.valid_until || undefined}
          />
        </Box>
        <Box>
          <Text size="2" weight="medium" as="div" mb="1">
            Valid until <Text size="1" color="gray">(optional)</Text>
          </Text>
          <DateField
            value={state.valid_until}
            onChange={(v) => setState({ ...state, valid_until: v })}
            placeholder="No expiry"
            minDate={state.valid_from || undefined}
          />
        </Box>
      </Grid>

      <Grid columns="3" gap="3" align="end">
        <Box>
          <Flex justify="between" align="center" mb="1">
            <Text size="2" weight="medium">Total cap</Text>
            <Flex align="center" gap="1">
              <Text size="1" color="gray">Unlimited</Text>
              <Switch
                size="1"
                checked={state.total_cap_unlimited}
                onCheckedChange={(checked) =>
                  setState({ ...state, total_cap_unlimited: checked })
                }
              />
            </Flex>
          </Flex>
          <TextField.Root
            type="number"
            min="0"
            value={state.total_redemption_cap}
            onChange={(e) =>
              setState({ ...state, total_redemption_cap: e.target.value })
            }
            placeholder="e.g. 1000"
            disabled={state.total_cap_unlimited}
          />
        </Box>
        <Box>
          <Flex justify="between" align="center" mb="1">
            <Text size="2" weight="medium">Per-user cap</Text>
            <Flex align="center" gap="1">
              <Text size="1" color="gray">Unlimited</Text>
              <Switch
                size="1"
                checked={state.per_user_cap_unlimited}
                onCheckedChange={(checked) =>
                  setState({ ...state, per_user_cap_unlimited: checked })
                }
              />
            </Flex>
          </Flex>
          <TextField.Root
            type="number"
            min="1"
            value={state.per_user_redemption_cap.toString()}
            onChange={(e) =>
              setState({
                ...state,
                per_user_redemption_cap: parseInt(e.target.value || "1", 10),
              })
            }
            disabled={state.per_user_cap_unlimited}
          />
        </Box>
        <Box>
          <Text size="2" weight="medium" as="div" mb="1">Status</Text>
          <Select
            value={state.status}
            onChange={(v) =>
              setState({ ...state, status: v as PromoFormState["status"] })
            }
            options={OFFER_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
          />
        </Box>
      </Grid>

      <EligibilitySection
        elig={state.eligibility}
        onChange={(e) => setState({ ...state, eligibility: e })}
        currentOfferId={currentOfferId ?? null}
      />
    </Flex>
  );
}

function EligibilitySection({
  elig,
  onChange,
  currentOfferId,
}: {
  elig: EligibilityFormState;
  onChange: (e: EligibilityFormState) => void;
  currentOfferId: string | null;
}) {
  const { data: plans } = useQuery<SubscriptionPlan[]>({
    queryKey: ["subscription-plans"],
    queryFn: () => anythinkPayService.getSubscriptionPlans(),
    enabled: elig.restricted,
  });
  const { data: allOffers } = useQuery<Offer[]>({
    queryKey: ["anythinkpay-offers", "list"],
    queryFn: () => anythinkPayService.getOffers(),
    enabled: elig.restricted,
  });
  const candidateOffers = (allOffers ?? [])
    .filter((o) => o.id !== currentOfferId);

  return (
    <Box style={{ borderTop: "1px solid var(--gray-a5)", paddingTop: 16 }}>
      <Flex justify="between" align="center" mb="2">
        <Flex direction="column">
          <Text size="2" weight="medium">
            <Lock size={12} style={{ marginRight: 4, marginBottom: -1 }} />
            Eligibility
          </Text>
          <Text size="1" color="gray">
            By default, anyone can redeem this code. Turn on restrictions to gate
            it.
          </Text>
        </Flex>
        <Flex align="center" gap="1">
          <Text size="1" color="gray">Restricted</Text>
          <Switch
            size="1"
            checked={elig.restricted}
            onCheckedChange={(checked) =>
              onChange({ ...elig, restricted: checked })
            }
          />
        </Flex>
      </Flex>

      {elig.restricted && (
        <Flex direction="column" gap="3" mt="2">
          <Flex align="center" gap="3">
            <Text size="1" color="gray">A user must match</Text>
            <Select
              value={elig.match}
              onChange={(v) =>
                onChange({ ...elig, match: v as "all" | "any" })
              }
              options={[
                { value: "all", label: "all rules below" },
                { value: "any", label: "any rule below" },
              ]}
            />
          </Flex>

          <EligibilityRuleRow
            enabled={elig.subscribed_to_plan_enabled}
            onToggle={(v) =>
              onChange({ ...elig, subscribed_to_plan_enabled: v })
            }
            label="Active subscription on specific plan(s)"
          >
            <PlanMultiSelect
              plans={plans ?? []}
              selected={elig.plan_ids}
              onChange={(ids) => onChange({ ...elig, plan_ids: ids })}
            />
          </EligibilityRuleRow>

          <EligibilityRuleRow
            enabled={elig.min_tier_enabled}
            onToggle={(v) => onChange({ ...elig, min_tier_enabled: v })}
            label="Minimum subscription tier"
          >
            <Flex align="center" gap="2">
              <Text size="2">Tier ≥</Text>
              <TextField.Root
                type="number"
                min="1"
                value={elig.min_tier_rank.toString()}
                onChange={(e) =>
                  onChange({
                    ...elig,
                    min_tier_rank: parseInt(e.target.value || "1", 10),
                  })
                }
                style={{ width: 80 }}
              />
              <Text size="1" color="gray">
                (set tier rank on each plan)
              </Text>
            </Flex>
          </EligibilityRuleRow>

          <EligibilityRuleRow
            enabled={elig.redeemed_offer_enabled}
            onToggle={(v) =>
              onChange({ ...elig, redeemed_offer_enabled: v })
            }
            label="Has previously redeemed a specific code"
          >
            <Select
              value={elig.offer_id || ""}
              onChange={(v) => onChange({ ...elig, offer_id: v })}
              options={[
                { value: "", label: "Choose an offer…" },
                ...candidateOffers.map((o) => ({
                  value: o.id,
                  label: o.name,
                })),
              ]}
            />
          </EligibilityRuleRow>
        </Flex>
      )}
    </Box>
  );
}

function EligibilityRuleRow({
  enabled,
  onToggle,
  label,
  children,
}: {
  enabled: boolean;
  onToggle: (v: boolean) => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Flex align="center" gap="2" mb="1">
        <Switch
          size="1"
          checked={enabled}
          onCheckedChange={onToggle}
        />
        <Text size="2">{label}</Text>
      </Flex>
      {enabled && <Box pl="6">{children}</Box>}
    </Box>
  );
}

function PlanMultiSelect({
  plans,
  selected,
  onChange,
}: {
  plans: SubscriptionPlan[];
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const activePlans = plans.filter((p) => p.is_active);
  if (activePlans.length === 0) {
    return (
      <Text size="1" color="gray">
        No subscription plans configured yet.
      </Text>
    );
  }
  return (
    <Flex direction="column" gap="1">
      {activePlans.map((p) => {
        const checked = selected.includes(p.id);
        return (
          <Flex key={p.id} align="center" gap="2">
            <Switch
              size="1"
              checked={checked}
              onCheckedChange={(v) =>
                onChange(
                  v
                    ? [...selected, p.id]
                    : selected.filter((id) => id !== p.id),
                )
              }
            />
            <Text size="2">{p.name}</Text>
            {p.tier_rank != null && (
              <Text size="1" color="gray">tier {p.tier_rank}</Text>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

function PromoDetailDrawer({
  row,
  onClose,
}: {
  row: PromoRow | null;
  onClose: () => void;
}) {
  const [tab, setTab] = useState("redemptions");
  const [editState, setEditState] = useState<PromoFormState | null>(null);
  const update = useUpdateAnythinkPayOffer();
  // Source the count from the live redemptions query — the row.code.redemption_count loaded
  // with the offer list goes stale the moment any user redeems (typically from the app, not
  // the admin UI). React Query dedupes against the Redemptions tab's own call.
  const { data: redemptions } = useAnythinkPayOfferRedemptions(row?.offer.id);
  const liveCount = redemptions?.length ?? row?.code?.redemption_count ?? 0;

  useEffect(() => {
    if (row) {
      setEditState(formFromOffer(row.offer));
      setTab("redemptions");
    }
  }, [row]);

  const submitEdit = async () => {
    if (!row || !editState) return;
    if (!editState.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const redeemerJson = serialiseReward(editState.redeemer);
    if (!redeemerJson) {
      toast.error("Choose a reward");
      return;
    }
    const totalCap = editState.total_cap_unlimited
      ? null
      : editState.total_redemption_cap.trim()
      ? parseInt(editState.total_redemption_cap, 10)
      : null;
    const perUserCap = editState.per_user_cap_unlimited
      ? UNLIMITED_PER_USER_CAP
      : editState.per_user_redemption_cap;

    try {
      await update.mutateAsync({
        id: row.offer.id,
        name: editState.name.trim(),
        redeemer_reward_json: redeemerJson,
        eligibility_json: eligibilityFormToJson(editState.eligibility),
        valid_from: editState.valid_from || null,
        valid_until: editState.valid_until || null,
        total_redemption_cap: totalCap,
        per_user_redemption_cap: perUserCap,
        status: editState.status,
      });
    } catch {
      // toast handled in service
    }
  };

  const copyCode = async () => {
    if (!row?.code?.slug) return;
    try {
      await navigator.clipboard.writeText(row.code.slug);
      toast.success("Code copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <SideDrawer
      isOpen={!!row}
      onClose={onClose}
      title={row?.code?.slug ?? row?.offer.name ?? ""}
      width="max-w-2xl"
    >
      {row && editState && (
        <Flex direction="column" gap="3">
          {row.code && (
            <Card>
              <Flex justify="between" align="center" gap="2">
                <Flex direction="column" gap="1">
                  <Text size="1" color="gray">Code</Text>
                  <Badge size="2" variant="soft" color="indigo" style={{ fontFamily: "monospace", alignSelf: "flex-start" }}>
                    {row.code.slug}
                  </Badge>
                </Flex>
                <Flex direction="column" gap="1" align="end">
                  <Text size="1" color="gray">Redemptions</Text>
                  <Text size="2" weight="medium">{liveCount}</Text>
                </Flex>
                <IconButton variant="soft" size="2" onClick={copyCode} title="Copy code">
                  <Copy size={14} />
                </IconButton>
              </Flex>
            </Card>
          )}

          <Tabs.Root value={tab} onValueChange={setTab}>
            <Tabs.List>
              <Tabs.Trigger value="redemptions">Redemptions</Tabs.Trigger>
              <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
            </Tabs.List>

            <Box mt="4">
              <Tabs.Content value="redemptions">
                <PromoRedemptionsTab offerId={row.offer.id} />
              </Tabs.Content>
              <Tabs.Content value="settings">
                <PromoFormBody
                  state={editState}
                  setState={setEditState}
                  currentOfferId={row.offer.id}
                />
                <Flex justify="end" mt="4">
                  <Button onClick={submitEdit} loading={update.isPending}>
                    Save changes
                  </Button>
                </Flex>
              </Tabs.Content>
            </Box>
          </Tabs.Root>
        </Flex>
      )}
    </SideDrawer>
  );
}

function PromoRedemptionsTab({ offerId }: { offerId: string }) {
  const { data: redemptions, isLoading } = useAnythinkPayOfferRedemptions(offerId);

  if (isLoading) return <LoadingScreen fullPage={false} />;
  if ((redemptions?.length ?? 0) === 0)
    return <Text size="2" color="gray">No redemptions yet.</Text>;

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Reward</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {(redemptions ?? []).map((r) => (
          <Table.Row key={r.id}>
            <Table.Cell>
              <Flex direction="column">
                <Text size="2" weight="medium">
                  {r.redeemer
                    ? `${r.redeemer.first_name} ${r.redeemer.last_name}`.trim()
                    : `#${r.redeemer_user_id}`}
                </Text>
                {r.redeemer?.email && (
                  <Text size="1" color="gray">{r.redeemer.email}</Text>
                )}
              </Flex>
            </Table.Cell>
            <Table.Cell>
              <Badge color={rewardStatusColor(r.redeemer_reward_status)} variant="soft">
                {r.redeemer_reward_status}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Text size="2" color="gray">{formatDate(r.redeemed_at)}</Text>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
